import type React from 'react';
import type { WorkspaceRole } from '@/hooks/use-active-workspace';
import {
  IconDashboard,
  IconChat,
  IconContacts,
  IconPipeline,
  IconGroups,
  IconCampaign,
  IconConnection,
  IconAutomation,
  IconBot,
  IconWrench,
  IconSummary,
  IconSettings,
  IconPhone,
  IconMembers,
  IconTeams,
  IconTag,
  IconIntegrations,
  IconWebhook,
  IconKey,
  IconSliders,
  IconCard,
  IconLayers,
  IconBriefcase,
  IconBuilding,
  IconReceipt
} from './sidebar-icons';

type NavIcon = () => React.ReactElement;

export type NavItemDef = {
  to: string;
  label: string;
  Icon: NavIcon;
  end: boolean;
  // Allowed roles. Undefined = visible for everyone.
  roles?: WorkspaceRole[];
};

export type NavSection = {
  id: string;
  // Sem rótulo = bloco principal, sem título.
  label?: string;
  items: NavItemDef[];
  // owner = só o dono do workspace ativo; agency = só contas com plano de revenda ativo.
  audience?: 'owner' | 'agency';
  // Recolhível: fechada por padrão, abre sozinha quando a página atual está dentro dela.
  collapsible?: { Icon: NavIcon };
};

export type NavViewer = {
  role: WorkspaceRole | null;
  isOwner: boolean;
  isAgency: boolean;
};

const CONFIG_ROLES: WorkspaceRole[] = ['owner', 'admin'];

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'main',
    items: [
      { to: '/', label: 'Visão geral', Icon: IconDashboard, end: true },
      { to: '/chat', label: 'Atendimento', Icon: IconChat, end: false },
      { to: '/contacts', label: 'Contatos', Icon: IconContacts, end: false },
      { to: '/pipelines', label: 'Pipeline', Icon: IconPipeline, end: false }
    ]
  },
  {
    id: 'groups',
    label: 'Grupos',
    items: [
      { to: '/groups', label: 'Grupos', Icon: IconGroups, end: false },
      { to: '/campaigns', label: 'Campanhas', Icon: IconCampaign, end: false, roles: CONFIG_ROLES },
      { to: '/group-links', label: 'Links rotativos', Icon: IconConnection, end: false, roles: CONFIG_ROLES }
    ]
  },
  {
    id: 'automation',
    label: 'Automação',
    items: [
      { to: '/automations', label: 'Automações', Icon: IconAutomation, end: false, roles: CONFIG_ROLES },
      { to: '/agents', label: 'Agentes de IA', Icon: IconBot, end: false, roles: CONFIG_ROLES },
      { to: '/functions', label: 'Funções', Icon: IconWrench, end: false, roles: CONFIG_ROLES },
      { to: '/summaries', label: 'Resumos', Icon: IconSummary, end: false, roles: CONFIG_ROLES }
    ]
  },
  {
    id: 'settings',
    label: 'Configurações',
    collapsible: { Icon: IconSettings },
    items: [
      { to: '/connection', label: 'Conexão WhatsApp', Icon: IconPhone, end: false, roles: CONFIG_ROLES },
      { to: '/members', label: 'Membros', Icon: IconMembers, end: false },
      { to: '/teams', label: 'Equipes', Icon: IconTeams, end: false, roles: CONFIG_ROLES },
      { to: '/personalization', label: 'Campos e tags', Icon: IconTag, end: false, roles: CONFIG_ROLES },
      { to: '/integrations', label: 'Integrações', Icon: IconIntegrations, end: false, roles: CONFIG_ROLES },
      { to: '/webhooks', label: 'Webhooks', Icon: IconWebhook, end: false, roles: CONFIG_ROLES },
      { to: '/api-keys', label: 'API', Icon: IconKey, end: false, roles: CONFIG_ROLES },
      { to: '/workspace/settings', label: 'Workspace', Icon: IconSliders, end: false, roles: CONFIG_ROLES }
    ]
  },
  {
    id: 'account',
    label: 'Conta',
    audience: 'owner',
    items: [
      { to: '/assinatura', label: 'Assinatura', Icon: IconCard, end: true },
      { to: '/workspaces', label: 'Meus workspaces', Icon: IconLayers, end: true }
    ]
  },
  {
    id: 'agency',
    label: 'Agência',
    audience: 'agency',
    items: [
      { to: '/agencia', label: 'Painel', Icon: IconBriefcase, end: true },
      { to: '/agencia/workspaces', label: 'Workspaces', Icon: IconBuilding, end: false },
      { to: '/agencia/billing', label: 'Cobrança', Icon: IconReceipt, end: false }
    ]
  }
];

function hasRoleAccess(allowed: WorkspaceRole[] | undefined, role: WorkspaceRole | null): boolean {
  if (!allowed) return true;
  if (!role) return false;
  return allowed.includes(role);
}

function hasAudience(audience: NavSection['audience'], viewer: NavViewer): boolean {
  if (audience === 'owner') return viewer.isOwner;
  if (audience === 'agency') return viewer.isAgency;
  return true;
}

// Seções que o usuário vê, só com os itens que o papel dele permite; seção vazia some.
export function visibleNavSections(viewer: NavViewer): NavSection[] {
  return NAV_SECTIONS
    .filter((section) => hasAudience(section.audience, viewer))
    .map((section) => ({ ...section, items: section.items.filter((item) => hasRoleAccess(item.roles, viewer.role)) }))
    .filter((section) => section.items.length > 0);
}

export function pathMatches(pathname: string, to: string, end: boolean): boolean {
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

// Escolha manual no título de uma seção recolhível; `at` é o location.key da navegação em que foi feita.
export type SectionToggle = { open: boolean; at: string };

// Recolhível abre sozinha quando contém a página atual. Abrir à mão vale até fechar;
// fechar à mão vale até a próxima navegação.
export function isSectionOpen(
  section: NavSection,
  location: { pathname: string; key: string },
  toggle?: SectionToggle
): boolean {
  if (!section.collapsible) return true;
  if (toggle?.open) return true;
  if (toggle && toggle.at === location.key) return false;
  return section.items.some((item) => pathMatches(location.pathname, item.to, item.end));
}
